/**
 * Offline cache for GET API responses.
 *
 * Strategy
 * --------
 *  - Every successful GET response is stored in IndexedDB, keyed by full URL.
 *  - When a GET request fails because there is no network, we return the last
 *    cached copy (if any) with a synthetic `__offline: true` marker.
 *  - POST/PUT/DELETE are NEVER queued or cached — mutations require network.
 *    The UI shows a toast asking the coach to try again when back online.
 *
 * IndexedDB is used instead of localStorage because:
 *   - 5 MB soft limit on localStorage would fill up fast with images/PDF data.
 *   - IndexedDB is async, so it doesn't block the main thread.
 *
 * We only cache endpoints in ALLOWED_PATH_PREFIXES — this keeps the DB tiny
 * and avoids caching things that make no sense offline (auth, exports, etc.).
 */

const DB_NAME = "efcm-offline";
const STORE = "responses";
const VERSION = 1;

// Only these path prefixes are cached (path after /api).
// Everything else is network-only.
const ALLOWED_PATH_PREFIXES = [
  "/teams",
  "/players",
  "/trainings",
  "/matches",
  "/task-templates",
  "/rival-players",
  "/meta/",
  "/auth/me",
];

const MAX_ENTRIES = 500;

let _dbPromise = null;

function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB not available"));
      return;
    }
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "url" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

function txStore(mode) {
  return openDb().then((db) => db.transaction(STORE, mode).objectStore(STORE));
}

function normalisePath(url) {
  // Turn "https://.../api/players?team_id=X" into "/players?team_id=X"
  try {
    const u = new URL(url, window.location.origin);
    let p = u.pathname;
    if (p.startsWith("/api")) p = p.slice(4);
    return p + (u.search || "");
  } catch {
    return url;
  }
}

function isCacheable(url) {
  const path = normalisePath(url).split("?")[0];
  return ALLOWED_PATH_PREFIXES.some((p) => path.startsWith(p));
}

export async function cacheGet(url) {
  if (!isCacheable(url)) return null;
  try {
    const store = await txStore("readonly");
    return await new Promise((res) => {
      const req = store.get(url);
      req.onsuccess = () => res(req.result || null);
      req.onerror = () => res(null);
    });
  } catch {
    return null;
  }
}

export async function cachePut(url, data) {
  if (!isCacheable(url)) return;
  try {
    const store = await txStore("readwrite");
    store.put({ url, data, savedAt: new Date().toISOString() });
    // Enforce a rough size cap by trimming oldest entries.
    _maybeTrim();
  } catch {
    /* swallow — offline cache is best-effort */
  }
}

async function _maybeTrim() {
  try {
    const store = await txStore("readonly");
    const count = await new Promise((res) => {
      const req = store.count();
      req.onsuccess = () => res(req.result || 0);
      req.onerror = () => res(0);
    });
    if (count <= MAX_ENTRIES) return;

    // Gather all entries, sort by savedAt asc, delete the oldest overflow.
    const entries = await new Promise((res) => {
      const items = [];
      const req = store.openCursor();
      req.onsuccess = () => {
        const cur = req.result;
        if (cur) {
          items.push(cur.value);
          cur.continue();
        } else {
          res(items);
        }
      };
      req.onerror = () => res([]);
    });
    entries.sort((a, b) => (a.savedAt || "").localeCompare(b.savedAt || ""));
    const overflow = entries.length - MAX_ENTRIES;
    const wStore = await txStore("readwrite");
    for (let i = 0; i < overflow; i++) wStore.delete(entries[i].url);
  } catch {
    /* ignore */
  }
}

export async function cacheClear() {
  try {
    const store = await txStore("readwrite");
    store.clear();
  } catch {
    /* ignore */
  }
}

export function isNetworkError(err) {
  if (!err) return false;
  // Axios sets err.code === "ERR_NETWORK" for CORS/DNS/offline failures.
  if (err.code === "ERR_NETWORK") return true;
  if (err.message === "Network Error") return true;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  // Fetches to /api that return no `err.response` are typically network drops.
  if (!err.response && err.request) return true;
  return false;
}
